import {
  ComponentType,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Bar, BarItem, BarItemProps } from "@nivo/bar";
import {
  animated,
  SpringConfig,
  SpringValues,
  to,
  useSpring,
} from "@react-spring/web";
import { buildClasses } from "../utils/buildClasses";
import { RyeChartTooltipProvider } from "../rye/RyeChartTooltip";
import { RyeIcon } from "../rye/RyeIcon";
import {
  GroupedBarGroup,
  GroupedBarRow,
  visibleBarRows,
} from "./groupedBarRows";

/**
 * Everything that moves when a group expands or collapses — bars, labels, and
 * the chart's height — runs on this one spring, so it all moves together. It's
 * critically damped: quick, and it never overshoots, which would carry a bar
 * into its neighbor's row. Bar contents that animate on their own should use it
 * too.
 */
export const GROUPED_BAR_MOTION: SpringConfig = { tension: 300, friction: 35 };

const ROW_HEIGHT_PX = 34;
// Nivo's band padding, as a fraction of a row. It applies inside and around the
// rows, so the chart is sized to the row count plus this to keep the spacing
// between rows exactly ROW_HEIGHT_PX however many are showing.
const BAR_PADDING = 0.3;
const LABEL_COLUMN_WIDTH_PX = 220;
const MARGIN = { top: 8, left: LABEL_COLUMN_WIDTH_PX };
// Room below the bars for the value axis, or just a little space without one.
const BOTTOM_MARGIN_PX = { withAxis: 32, withoutAxis: 8 };
// Appended to a color's hex to fade the bars that aren't selected.
const UNSELECTED_ALPHA_HEX = "59"; // ~35%

const AXIS_TEXT_COLOR = "#64748b"; // on-surface-500
const GRID_LINE_COLOR = "#e2e8f0"; // on-surface-200
const VALUE_TICK_COUNT = 4;

export type GroupedBarDatum = { id: string; value: number };

/** What a chart's own bar and tooltip components can read about its rows. */
export interface GroupedBarsInfo<Row extends GroupedBarRow> {
  /** Every row, including the categories of collapsed groups. */
  allRowsById: Map<string, Row>;
  expandedGroupIds: ReadonlySet<string>;
  selectedId: string | null;
  /** The width of the bar area, between the labels and the right margin. */
  innerWidth: number;
}

interface GroupedBarsContextValue extends GroupedBarsInfo<GroupedBarRow> {
  /** Each showing row's position, top to bottom. */
  indexById: Map<string, number>;
  /** The same as of the previous render, to tell where rows are coming from. */
  prevIndexById: Map<string, number>;
  expandableGroupIds: ReadonlySet<string>;
  onToggleGroup: (groupId: string) => void;
  onSelectRow: (row: GroupedBarRow) => void;
  BarContent: ComponentType<BarItemProps<GroupedBarDatum>>;
  Tooltip: ComponentType<{ row: GroupedBarRow }>;
  LabelIcon: ComponentType<{ row: GroupedBarRow }> | undefined;
}

/**
 * Nivo renders the row labels, bars, and tooltips as components, so they're
 * defined once at module level — a component recreated on every render would
 * be remounted, restarting its animation — and read their data from here.
 */
const GroupedBarsContext = createContext<GroupedBarsContextValue | null>(null);

function useGroupedBarsContext(): GroupedBarsContextValue {
  const value = useContext(GroupedBarsContext);
  if (!value) throw new Error("Needs GroupedBarsContext");
  return value;
}

/** For a chart's own bar and tooltip components. */
export function useGroupedBars<
  Row extends GroupedBarRow,
>(): GroupedBarsInfo<Row> {
  return useGroupedBarsContext() as unknown as GroupedBarsInfo<Row>;
}

/**
 * How far a row is drawn from the place Nivo puts it, so a group's categories
 * unfold out from under it and fold back up into it, like an accordion.
 *
 * Nivo grows an entering row in place and shrinks a leaving one in place,
 * while the rows below slide out of the way — through the space those rows
 * occupy. Offsetting entering categories to start at their group's previous
 * row, and leaving ones to end at their group's new row, means every row moves
 * the same direction and none crosses another. The offset runs on the same
 * spring as Nivo's, so the two stay in step.
 */
function useFoldOffset(rowId: string) {
  const { allRowsById, indexById, prevIndexById } = useGroupedBarsContext();
  const row = allRowsById.get(rowId);
  const isShown = indexById.has(rowId);

  // Measured once, when the row mounts. A chart drawn for the first time has
  // no previous positions, so its rows just appear in place.
  const [enterOffsetPx] = useState(() => {
    const groupPrev = row && prevIndexById.get(row.groupId);
    const own = indexById.get(rowId);
    return row?.kind === "category" &&
      !prevIndexById.has(rowId) &&
      groupPrev !== undefined &&
      own !== undefined
      ? (groupPrev - own) * ROW_HEIGHT_PX
      : 0;
  });

  // Measured once, when the row starts leaving; after that it isn't in either
  // position map.
  const leaveOffsetPx = useRef<number | null>(null);
  if (isShown) {
    leaveOffsetPx.current = null;
  } else if (leaveOffsetPx.current === null) {
    const group = row && indexById.get(row.groupId);
    const prev = prevIndexById.get(rowId);
    leaveOffsetPx.current =
      group !== undefined && prev !== undefined
        ? (group - prev) * ROW_HEIGHT_PX
        : 0;
  }

  const { offset } = useSpring({
    from: { offset: enterOffsetPx },
    to: { offset: isShown ? 0 : leaveOffsetPx.current },
    config: GROUPED_BAR_MOTION,
  });
  return to(offset, (dy) => `translate(0, ${dy})`);
}

/**
 * A row's label, as Nivo's axis tick. Nivo animates each tick's position and
 * fades it in and out on the same spring as the bars; applying those values is
 * what keeps a label alongside its bar while rows move.
 */
function RowLabel({
  value,
  animatedProps,
}: {
  // Only these of Nivo's tick props are needed. (Their type lives in
  // @nivo/axes, which isn't a direct dependency.)
  value: string;
  animatedProps: SpringValues<{ transform: string; opacity: number }>;
}): JSX.Element {
  const {
    allRowsById,
    expandedGroupIds,
    expandableGroupIds,
    onToggleGroup,
    onSelectRow,
    LabelIcon,
  } = useGroupedBarsContext();
  const foldOffset = useFoldOffset(value);
  const row = allRowsById.get(value);
  if (!row) return <g />;
  const isExpandable = expandableGroupIds.has(row.id);
  const isExpanded = expandedGroupIds.has(row.id);
  return (
    <animated.g
      transform={animatedProps.transform}
      opacity={animatedProps.opacity}
    >
      <animated.g transform={foldOffset}>
        {/* An HTML label, so long names can truncate and groups get a real
          button to expand them. */}
        <foreignObject
          x={-LABEL_COLUMN_WIDTH_PX}
          y={-ROW_HEIGHT_PX / 2}
          width={LABEL_COLUMN_WIDTH_PX - 12}
          height={ROW_HEIGHT_PX}
        >
          <button
            type="button"
            onClick={() =>
              isExpandable ? onToggleGroup(row.id) : onSelectRow(row)
            }
            aria-expanded={isExpandable ? isExpanded : undefined}
            aria-label={
              isExpandable
                ? `${isExpanded ? "Collapse" : "Expand"} ${row.label}`
                : `Show ${row.label} transactions`
            }
            className={buildClasses(
              "flex",
              "items-center",
              "gap-1",
              "w-full",
              "h-full",
              "text-left",
              "text-sm",
              "rounded-md",
              "outline-none",
              "focus-visible:outline",
              "focus-visible:outline-2",
              "focus-visible:outline-purple-500",
              {
                if: row.kind === "group",
                then: buildClasses("font-medium", "text-on-surface-800"),
                else: buildClasses("pl-7", "text-on-surface-600"),
              },
            )}
          >
            {isExpandable && (
              <RyeIcon
                name="keyboard_arrow_right"
                size="sm"
                className={buildClasses("shrink-0", "transition-transform", {
                  if: isExpanded,
                  then: "rotate-90",
                })}
              />
            )}
            {LabelIcon && <LabelIcon row={row} />}
            <span className={buildClasses("truncate")}>{row.label}</span>
          </button>
        </foreignObject>
      </animated.g>
    </animated.g>
  );
}

/** A chart's own bar component, moved with its row as groups unfold. */
function FoldedBar(props: BarItemProps<GroupedBarDatum>): JSX.Element {
  const { BarContent } = useGroupedBarsContext();
  const foldOffset = useFoldOffset(String(props.bar.data.indexValue));
  return (
    <animated.g transform={foldOffset}>
      <BarContent {...props} />
    </animated.g>
  );
}

function RowTooltip({ indexValue }: { indexValue: string | number }) {
  const { allRowsById, Tooltip } = useGroupedBarsContext();
  const row = allRowsById.get(String(indexValue));
  return row ? <Tooltip row={row} /> : null;
}

/**
 * The chart's height, animated on the shared spring so the rows below a
 * collapsing group slide up into view rather than being cut off.
 *
 * Returns the height to show, and the taller height to draw the chart at in
 * the meantime: while collapsing, the chart keeps its old height until the
 * rows settle, with the difference added as bottom margin so the rows don't
 * spread out to fill it.
 */
function useSettlingHeight(targetPx: number) {
  const [drawnPx, setDrawnPx] = useState(targetPx);
  const { height } = useSpring({
    height: targetPx,
    config: GROUPED_BAR_MOTION,
    onRest: () => setDrawnPx(targetPx),
  });
  return { shownHeight: height, drawnPx: Math.max(drawnPx, targetPx) };
}

/**
 * The height a chart with this many rows settles at. The chart animates to
 * it, so this is for laying out around where it's headed.
 */
export function groupedBarChartHeightPx(
  rowCount: number,
  { valueAxis }: { valueAxis: boolean },
): number {
  return (
    (rowCount + BAR_PADDING) * ROW_HEIGHT_PX +
    MARGIN.top +
    (valueAxis ? BOTTOM_MARGIN_PX.withAxis : BOTTOM_MARGIN_PX.withoutAxis)
  );
}

/**
 * Horizontal bars for category groups and their categories, with the groups
 * expandable. Expanding a group just adds its categories as rows, so the chart
 * stays a plain Nivo bar chart; as it does, the categories unfold out from
 * under their group and everything moves on one spring.
 *
 * Each chart supplies its rows' values and colors, and its own bar contents
 * and tooltip as module-level components, which read the rows through
 * useGroupedBars.
 */
export function GroupedBarChart<Row extends GroupedBarRow>({
  groups,
  expandedGroupIds,
  onToggleGroup,
  selectedId,
  onSelectRow,
  getValue,
  getColor,
  barContent = BarItem,
  tooltip,
  labelIcon,
  formatAxisValue,
  marginRight,
  ariaLabel,
}: {
  groups: GroupedBarGroup<Row>[];
  expandedGroupIds: ReadonlySet<string>;
  onToggleGroup: (groupId: string) => void;
  selectedId: string | null;
  onSelectRow: (row: Row) => void;
  /** The length of a row's bar. */
  getValue: (row: Row) => number;
  getColor: (row: Row) => string;
  /** Draws a bar; Nivo's own bar unless a chart needs more. */
  barContent?: ComponentType<BarItemProps<GroupedBarDatum>>;
  tooltip: ComponentType<{ row: Row }>;
  /** Drawn just before a row's label. */
  labelIcon?: ComponentType<{ row: Row }>;
  /** Labels the value axis; without it, there's no axis or grid lines. */
  formatAxisValue?: (value: number) => string;
  /** Room to the right of the bars, for whatever a chart draws there. */
  marginRight: number;
  ariaLabel: string;
}): JSX.Element {
  const rows = useMemo(
    () => visibleBarRows(groups, expandedGroupIds),
    [groups, expandedGroupIds],
  );
  // Nivo stacks horizontal bars from the bottom up, so the rows go in
  // reversed to read top to bottom.
  const barData = useMemo<GroupedBarDatum[]>(
    () => rows.map((row) => ({ id: row.id, value: getValue(row) })).reverse(),
    [rows, getValue],
  );
  // With every showing bar at zero, the scale would have nothing to span and
  // would put zero mid-chart.
  const hasAnyBar = barData.some((datum) => datum.value > 0);

  const allRowsById = useMemo(
    () =>
      new Map<string, Row>(
        groups.flatMap(({ categories, ...group }) =>
          [group as unknown as Row, ...categories].map(
            (row) => [row.id, row] as const,
          ),
        ),
      ),
    [groups],
  );
  const expandableGroupIds = useMemo(
    () =>
      new Set(
        groups
          .filter((group) => group.categories.length > 0)
          .map((group) => group.id),
      ),
    [groups],
  );
  const indexById = useMemo(
    () => new Map(rows.map((row, index) => [row.id, index])),
    [rows],
  );
  // Read during render, so it still holds the positions from before this one.
  const prevIndexRef = useRef(new Map<string, number>());
  const prevIndexById = prevIndexRef.current;
  useEffect(() => {
    prevIndexRef.current = indexById;
  }, [indexById]);

  // Sized here rather than by ResponsiveBar, which only learns a new height
  // from a resize observer a frame after rows change, and so draws one frame
  // squeezed into the old height. The height follows from the row count; only
  // the width needs measuring.
  const containerRef = useRef<HTMLDivElement>(null);
  const [widthPx, setWidthPx] = useState(0);
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () => setWidthPx(container.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const hasValueAxis = formatAxisValue !== undefined;
  const bottomMarginPx = hasValueAxis
    ? BOTTOM_MARGIN_PX.withAxis
    : BOTTOM_MARGIN_PX.withoutAxis;
  const targetHeightPx = groupedBarChartHeightPx(rows.length, {
    valueAxis: hasValueAxis,
  });
  const { shownHeight, drawnPx } = useSettlingHeight(targetHeightPx);

  const context = useMemo<GroupedBarsContextValue>(
    () => ({
      allRowsById: allRowsById as Map<string, GroupedBarRow>,
      expandedGroupIds,
      selectedId,
      innerWidth: Math.max(0, widthPx - MARGIN.left - marginRight),
      indexById,
      prevIndexById,
      expandableGroupIds,
      onToggleGroup,
      onSelectRow: onSelectRow as (row: GroupedBarRow) => void,
      BarContent: barContent,
      Tooltip: tooltip as ComponentType<{ row: GroupedBarRow }>,
      LabelIcon: labelIcon as ComponentType<{ row: GroupedBarRow }> | undefined,
    }),
    [
      allRowsById,
      expandedGroupIds,
      selectedId,
      widthPx,
      marginRight,
      indexById,
      prevIndexById,
      expandableGroupIds,
      onToggleGroup,
      onSelectRow,
      barContent,
      tooltip,
      labelIcon,
    ],
  );

  return (
    <div ref={containerRef}>
      <animated.div
        style={{ height: shownHeight }}
        className={buildClasses("overflow-hidden")}
      >
        <RyeChartTooltipProvider
          style={{ height: `${drawnPx}px` }}
          className={buildClasses("[&_rect]:cursor-pointer")}
        >
          <GroupedBarsContext.Provider value={context}>
            {/* Drawn only once the width is known. Drawn at zero width, the
                chart's first frame has a reversed axis, and the bars then
                animate out of that. */}
            {widthPx > 0 && (
              <Bar<GroupedBarDatum>
                width={widthPx}
                height={drawnPx}
                motionConfig={GROUPED_BAR_MOTION}
                data={barData}
                keys={["value"]}
                indexBy="id"
                layout="horizontal"
                valueScale={{
                  type: "linear",
                  min: 0,
                  max: hasAnyBar ? "auto" : 1,
                }}
                margin={{
                  ...MARGIN,
                  right: marginRight,
                  bottom: bottomMarginPx + drawnPx - targetHeightPx,
                }}
                padding={BAR_PADDING}
                borderRadius={3}
                colors={(bar) => {
                  const row = allRowsById.get(String(bar.indexValue));
                  const color = row ? getColor(row) : "#cbd5e1";
                  return selectedId !== null && bar.indexValue !== selectedId
                    ? `${color}${UNSELECTED_ALPHA_HEX}`
                    : color;
                }}
                enableLabel={false}
                enableGridX={hasValueAxis}
                enableGridY={false}
                gridXValues={VALUE_TICK_COUNT}
                axisLeft={{ tickSize: 0, renderTick: RowLabel }}
                axisBottom={
                  formatAxisValue
                    ? {
                        tickSize: 0,
                        tickPadding: 8,
                        tickValues: VALUE_TICK_COUNT,
                        format: (value) => formatAxisValue(Number(value)),
                      }
                    : null
                }
                theme={{
                  tooltip: {
                    container: {
                      background: "transparent",
                      boxShadow: "none",
                      padding: 0,
                    },
                  },
                  text: {
                    fontFamily: "InterVariable, sans-serif",
                    fontSize: 11,
                    fill: AXIS_TEXT_COLOR,
                  },
                  grid: { line: { stroke: GRID_LINE_COLOR, strokeWidth: 1 } },
                }}
                barComponent={FoldedBar}
                tooltip={RowTooltip}
                onClick={(bar) => {
                  const row = allRowsById.get(String(bar.indexValue));
                  if (row) onSelectRow(row);
                }}
                role="img"
                ariaLabel={ariaLabel}
              />
            )}
          </GroupedBarsContext.Provider>
        </RyeChartTooltipProvider>
      </animated.div>
    </div>
  );
}
